import React, { useContext, useEffect, useMemo } from 'react';
import { useStore } from 'react-redux';
import Helmet from 'react-helmet';
import 'semantic-ui-css/semantic.min.css';
import { useIntl, FormattedMessage } from 'react-intl';
import { Tab } from 'semantic-ui-react';

import loadSharedUserConfigFromAPI from 'app/redux/sharedUserConfig/loadUserConfigFromAPI';
import { SharedUserConfigurationAPIContext } from 'app/interfaces/SharedUserConfiguration';

import { TAB_DEFINITIONS } from './tabDefinitions';
import type { SettingDefinition, TabDefinition } from './tabDefinitions';
import SettingsRow from './SettingsRow';

import './style.scss';

function SettingsTabContent({ settings }: { settings: SettingDefinition[] }) {
  const visibleSettings = settings.filter((s) => s.isVisible?.() ?? true);

  return (
    <div className="settings-tab-content">
      {visibleSettings.map((setting, index) => {
        const Component = setting.component;
        const isLast = index === visibleSettings.length - 1;

        if (setting.type === 'content') {
          return <Component key={setting.key} />;
        }

        return (
          <SettingsRow
            key={setting.key}
            title={<FormattedMessage id={setting.titleId} />}
            noDivider={isLast}
          >
            <Component />
          </SettingsRow>
        );
      })}
    </div>
  );
}

function renderTabContent(tab: TabDefinition) {
  return <SettingsTabContent settings={tab.settings} />;
}

function SettingsPage() {
  const intl = useIntl();
  const store = useStore();
  const sharedUserConfigurationAPI = useContext(SharedUserConfigurationAPIContext);

  useEffect(() => {
    loadSharedUserConfigFromAPI(store, sharedUserConfigurationAPI);
  }, [store, sharedUserConfigurationAPI]);

  const panes = useMemo(
    () =>
      TAB_DEFINITIONS.filter((tab) => tab.isVisible()).map((tab) => ({
        menuItem: intl.formatMessage({ id: tab.titleId }),
        render: () => <Tab.Pane key={tab.key}>{renderTabContent(tab)}</Tab.Pane>,
      })),
    [intl]
  );

  return (
    <div className="settings-page">
      <Helmet title={intl.formatMessage({ id: 'settings.pageTitle' })} />
      <div className="ui container" style={{ paddingTop: '40px' }}>
        <h2 className="settings-page__header">
          <FormattedMessage id="settings.pageTitle" />
        </h2>
        <Tab panes={panes} />
      </div>
    </div>
  );
}

export default SettingsPage;
